import {
  buildCover,
  buildThemeFromTemplate,
  buildTypography,
  parseBlocksFromSiteplus
} from '@/constants/siteplusBlocks.js'
import {
  getTemplateById
} from '@/helpers/updateTemplateUtil.js'

export const get = (obj, path, defaultValue = undefined) => {
  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }

  return value;
}

const transformCoverToElements = (originalCover) => {
  return {
    elements: Object.entries(originalCover)
      .filter(([key]) => key !== 'cover') // Exclude 'cover' if it exists
      .map(([key, value]) => ({ [key]: value }))
  };
};

// const transformHeaderToElements = (header) => {
//   return {
//     elements: Object.entries(header).map(([key, value]) => ({ [key]: value }))
//   }
// }

export const createSiteplusFromTemplate = (data) => {
  const { templateCategoryData, websiteDetails, contentStructureData, selectedTemplate, template, selectedPalette, sections, productInstanceId, accessToken, isGrid, recommendedApps, selectedTone, layout, pages, earlyGeneration } = data

  const cover = buildCover(contentStructureData, template, selectedTemplate?.id)
  const header = {
    "logo": {
      "src": template?.logo?.src,
      ...(selectedTemplate?.version === "v1" && { fontFamily: template?.logo?.fontFamily }),
      "text": template?.logo?.text
    },
    'title': {
      "content": template.sections.header.title,
      "max_tokens": 5
    }
  }

  const transformedBlocks = () => {
    const blocks = {
      header: header
    }
    const layoutIndex = Number.isFinite(parseInt(layout, 10)) ? parseInt(layout, 10) : 0
    const coverBlockKey =
      selectedTemplate?.templates?.[layoutIndex]?.coverId ??
      (layoutIndex > 0 ? `cover${layoutIndex + '1'}` : 'cover')
    if (isGrid) {
      // buildCover already returns { elements: [...] } for multi-slide templates; do not wrap again
      blocks[coverBlockKey] = Array.isArray(cover?.elements)
        ? cover
        : { ...transformCoverToElements(cover) }
    } else {
      blocks.cover = cover
    }
    return blocks
  }

  const updateTemplateId = (template, isGrid) => {
    if (isGrid && template?.templates?.[layout]?.id) {
      return template?.templates[layout]?.id
    }

    const templateMapping = {
      "Cleaning Services": {
        current: "9937a286-32ea-4e6e-88f4-7446c4dc738f",
        grid: "5a0b05ac-999b-4b6d-a3e9-6b1f1de4a95f"
      },
      "Spa": {
        current: "6e2b0262-980d-4ada-b5ab-ec77816220e3",
        grid: "7c2b7460-8a73-47ed-9d4f-e1b7f00ecccb"
      },
      "Parenting Blog": {
        current: "26ae90a0-2d33-4841-876b-b656bbbb707d",
        grid: "88af2990-cee5-481d-844e-ee275bb78e7e"
      },
      "Career Services": {
        current: "7d4cf99b-c2bc-4790-996c-ba9f6bfd1bb7",
        grid: "edf922c9-4fe3-4537-8729-8c440fde135c"
      },
      "Beauty Store": {
        current: "23210ee7-ef96-40f7-9e34-52fd9385e5cd",
        grid: "14fe3082-cf99-4718-bc7b-79be048e2ed9"
      }
    };
    const templateName = template?.name

    if (templateMapping[templateName]) {
      return templateMapping[templateName][isGrid ? "grid" : "current"];
    }

    return template?.id
  }

  const payload = {
    'accessToken': accessToken,
    'productInstanceId': productInstanceId,
    'type': 'ai',
    'templateId': updateTemplateId(selectedTemplate, isGrid),
    'categoryName': templateCategoryData?.category,
    'unique': websiteDetails?.data?.title, //Title Of the website
    "prompt": websiteDetails?.data?.prompt,
    "title": websiteDetails?.data?.category, //Category of the website AI,
    "writingTone": selectedTone === "Custom" ? "Neutral" : selectedTone,
    'blocks': transformedBlocks(),
    'theme': buildThemeFromTemplate(selectedPalette),
    'typography': buildTypography(template),
    'sections': {
      'contact': true,
      'testimonials': true,
      'aboutUs': true,
      'portfolio': false,
      'footerGrid': true,
      'cover': true,
      'dashstore': sections.products || false,
      'dashbooking': sections.services || false,
      'store': sections.store || false
    },
    enableBlog: recommendedApps?.find(x => x.id === "blog")?.enabled ?? false,
    'isTrial': false,
    'pages': pages,
    ...(earlyGeneration && { 'earlyGeneration': true }),
    ...(isGrid && { 'newOnboarding': true })
  }

  // Just for cleaning services
  if (
    [
      "9937a286-32ea-4e6e-88f4-7446c4dc738f",
      "6251d8ed-96c6-41f5-8097-b55472c062cc",
    ].includes(selectedTemplate?.id)
  ) {
    payload.unique = template?.sections?.hero?.title;
  }

  return payload
}

/**
 * Minimal POST body for grid early sitewizard creation (before full onboarding payload exists).
 * Matches sitewizard-gw earlyGeneration contract.
 */
export const buildEarlyGridSitewizardCreatePayload = ({
  accessToken,
  productInstanceId,
  templateCategoryData,
  websiteDetails,
  selectedTemplate,
  recommendedApps,
  layout = 0,
  template,
}) => {
  const templateId = selectedTemplate?.templates?.[layout]?.id

  let unique = websiteDetails?.data?.title
  if (
    [
      '9937a286-32ea-4e6e-88f4-7446c4dc738f',
      '6251d8ed-96c6-41f5-8097-b55472c062cc',
    ].includes(selectedTemplate?.id)
  ) {
    unique = template?.sections?.hero?.title ?? unique
  }

  return {
    accessToken,
    productInstanceId,
    templateId,
    type: 'ai',
    enableBlog: recommendedApps?.find((x) => x.id === 'blog')?.enabled ?? false,
    isTrial: false,
    categoryName: templateCategoryData?.category ?? 'Services',
    unique,
    prompt: websiteDetails?.data?.prompt,
    title: websiteDetails?.data?.category,
    newOnboarding: true,
    earlyGeneration: true,
  }
}

export const finalizeSitePayload = (accessToken) => {
  return {
    'accessToken': accessToken,
    "aiFinalStep": true,
  }
}

export const parseTemplateFromSiteplus = (data, restData) => {
   const {
    id,
    productInstanceId,
    onboardingJson: {
      templateStyle,
      templateId,
      photoCategory,
      categoryName,
      unique,
      prompt,
      title,
      blocks,
      theme,
      typography,
      sections,
      step,
      subStep,
      coverImageId,
      ...rest
    },
  } = data

  let recreatedState = {
    id,
    productInstanceId
  }

  recreatedState.templateCategoryData = { category: categoryName, style: templateStyle, photoCategory }
  recreatedState.templateStyle = templateStyle
  recreatedState.websiteDetails = {
    title: unique,
    prompt: prompt,
    category: title,
    writingTone: rest.selectedTone
  }

  recreatedState.sectionsState = restData.sections

  const template = getTemplateById(templateId)

  recreatedState.selectedTemplate = template
  recreatedState.contentStructureData = parseBlocksFromSiteplus(blocks)
  
  if (blocks.header.logo) {
    recreatedState.logo = blocks.header.logo.src
  }
  
  const coverImage = blocks.cover.elements ? recreatedState.contentStructureData.blocks.cover.image : blocks.cover.image

  if (coverImage) {
    recreatedState.coverImage = {
      url: coverImage.url,
      id: coverImageId,
    }
  }

  recreatedState.selectedPalette = theme

  const font = { 
    header: typography.headingFont.fontFamily,
    body: typography.textFont.fontFamily,
    custom: typography.isCustom
  }

  recreatedState.selectedFont = font
  
  recreatedState.step = step
  recreatedState.subStep = subStep

  recreatedState = {...recreatedState, ...rest}
  return recreatedState
}

const SECTION_TO_PAGE_KEY = {
  appointments: 'booking',
  contact_form: 'contact',
  testimonials: 'reviews',
  about: 'aboutus',
  faq: 'faqs',
}

export const buildPagesFromCheckedSections = (checkedSortedSections) => {
  if (!Array.isArray(checkedSortedSections)) return undefined
  return checkedSortedSections.reduce((acc, item) => {
    if (item.id === 'header_and_introduction' || item.id === 'footer') return acc
    const key = SECTION_TO_PAGE_KEY[item.id] || item.id
    acc[key] = item.checked
    return acc
  }, {})
}